// Localização: frontend/src/components/Financeiro/ResumoFinanceiro.jsx
// Componente para exibir resumo estatístico de contas a pagar/receber

import React from 'react';
import { DollarSign, AlertCircle, Calendar, TrendingUp, Hash } from 'lucide-react';

const ResumoFinanceiro = ({ contas, tipo = 'pagar' }) => {
    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor || 0);
    };

    const isHoje = (data) => {
        if (!data) return false;
        const hoje = new Date();
        const dataVenc = new Date(data);
        return (
            dataVenc.getDate() === hoje.getDate() &&
            dataVenc.getMonth() === hoje.getMonth() &&
            dataVenc.getFullYear() === hoje.getFullYear()
        );
    };

    const isFuturo = (data) => {
        if (!data) return false;
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const dataVenc = new Date(data);
        dataVenc.setHours(0, 0, 0, 0);
        return dataVenc > hoje;
    };

    const isAtrasada = (conta) => {
        if (!conta.data_vencimento) return false;
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const dataVenc = new Date(conta.data_vencimento);
        dataVenc.setHours(0, 0, 0, 0);

        const statusAberto = tipo === 'pagar'
            ? (conta.status !== 'Pago' && conta.status !== 'Cancelado')
            : (conta.status !== 'Recebido' && conta.status !== 'Cancelado');

        return dataVenc < hoje && statusAberto;
    };

    // Cálculos
    const quantidade = contas.length;
    const atrasadas = contas.filter(c => isAtrasada(c)).length;
    const vencHoje = contas.filter(c => isHoje(c.data_vencimento)).length;
    const vencFuturos = contas.filter(c => isFuturo(c.data_vencimento)).length;
    const total = contas.reduce((sum, c) => sum + parseFloat(c.valor_total || 0), 0);

    const cards = [
        {
            label: 'Quantidade',
            value: quantidade,
            icon: Hash,
            color: 'blue',
            format: 'number'
        },
        {
            label: 'Atrasadas',
            value: atrasadas,
            icon: AlertCircle,
            color: 'red',
            format: 'number'
        },
        {
            label: 'Venc. Hoje',
            value: vencHoje,
            icon: Calendar,
            color: 'yellow',
            format: 'number'
        },
        {
            label: 'Venc. Futuros',
            value: vencFuturos,
            icon: TrendingUp,
            color: 'green',
            format: 'number'
        },
        {
            label: 'Total',
            value: total,
            icon: DollarSign,
            color: 'emerald',
            format: 'currency'
        }
    ];

    const getColorClasses = (color) => {
        const colors = {
            blue: 'bg-blue-50 text-blue-700 border-blue-200',
            red: 'bg-red-50 text-red-700 border-red-200',
            yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
            green: 'bg-green-50 text-green-700 border-green-200',
            emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200'
        };
        return colors[color] || colors.blue;
    };

    return (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {cards.map((card, index) => {
                const Icon = card.icon;
                const displayValue = card.format === 'currency'
                    ? formatarMoeda(card.value)
                    : card.value;

                return (
                    <div
                        key={index}
                        className={`p-4 rounded-lg border-2 ${getColorClasses(card.color)} transition-all hover:shadow-md`}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium uppercase tracking-wide opacity-75">
                                {card.label}
                            </span>
                            <Icon className="h-4 w-4 opacity-75" />
                        </div>
                        <div className="text-2xl font-bold">
                            {displayValue}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ResumoFinanceiro;
